/**
 * @lines98/client/services/local-auth — email + пароль регистрация/вход
 * (ADR-0006 rev3 §1; spec email-registration.md §2.2/§2.3/§4).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: третий путь auth к тому же player_id (рядом с
 * `oauth-login`). По образцу `oauth-login.ts`, но без redirect — это локальный
 * POST. Оба эндпоинта на успехе обновляют identity через `identity.update(
 * {playerId, secretToken})` (как OAuth callback) → следующий запрос уже под новым
 * Bearer. Клиент НЕ хранит email/пароль после сабмита (spec §4) — только
 * IdentityRecord (это делает identity.ts).
 *
 * SCOPE: register() (под Bearer текущего identity — boot если нет) + login() (без
 * Bearer-требования, но api-client всё равно приложит существующий). Маппинг
 * серверных reason → дискриминированный результат; человекочитаемый RU-текст —
 * на UI (components/auth-panel §2.4), здесь только коды. NOT IN SCOPE: форма/UI,
 * logout (это identity.clear + bootIdentity на стороне profile-screen).
 *
 * Cross-references:
 *   - ADR-0006 rev3 §1 (shape + reason-коды).
 *   - spec §2.2/§2.3/§2.4 (поля + маппинг ошибок), §4 (данные), AC-3/AC-5/AC-6.
 *   - services/identity.ts (update — обновление Bearer + playerId), oauth-login.ts (образец).
 *   - services/api-client.ts (envelope + Bearer injection).
 */

import { apiClient } from './api-client.js';
import { bootIdentity, getCurrent, update as updateIdentity } from './identity.js';

// =============================================================================
// Types
// =============================================================================

/** Серверный reason-код регистрации (ADR rev3 §1). UI мапит в RU-текст (§2.4). */
export type RegisterErrorReason =
  | 'invalid_email'
  | 'weak_password'
  | 'invalid_display_name'
  | 'email_taken'
  | 'no_identity' // клиентский: не удалось получить Bearer (identity mint провалился)
  | 'unauthorized'
  | 'unavailable'
  | 'server_error'
  | 'network';

/** Серверный reason-код входа (ADR rev3 §1). 401 — всегда обезличенный (§5/AC-6). */
export type LoginErrorReason =
  | 'credentials_invalid' // wrong-email ИЛИ wrong-password ИЛИ lock (uniform, AC-6)
  | 'malformed_request'
  | 'unavailable'
  | 'server_error'
  | 'network';

/** Успех register/login: identity уже обновлён; UI рисует «залогинен». */
export interface LocalAuthSuccess {
  readonly ok: true;
  readonly playerId: string;
  /** Заданный ник (register) или с сервера (login; может быть null у legacy). */
  readonly displayName: string | null;
}

export interface RegisterFailure {
  readonly ok: false;
  readonly reason: RegisterErrorReason;
}
export interface LoginFailure {
  readonly ok: false;
  readonly reason: LoginErrorReason;
}

export type RegisterResult = LocalAuthSuccess | RegisterFailure;
export type LoginResult = LocalAuthSuccess | LoginFailure;

interface LocalAuthWire {
  playerId: string;
  secretToken: string;
  displayName: string | null;
}

/**
 * Wire-форма register (Вариант B, owner-decision 2026-06-21): БЕЗ displayName — ник при
 * регистрации не задаётся (остаётся NULL, задаётся в Профиле). login по-прежнему отдаёт
 * displayName (возврат существующего аккаунта).
 */
interface RegisterWire {
  playerId: string;
  secretToken: string;
}

interface ServerErrorBody {
  reason?: string;
}

/**
 * Маскирует почту для строки-статуса своего профиля (Q-3): `andrey@gmail.com` →
 * `a•••@gmail.com`. Сохраняем ТОЛЬКО эту masked-форму (полную почту не храним —
 * spec §4 / I-3). Возвращает null для невалидного ввода (без `@`).
 */
function maskEmail(raw: string): string | null {
  const email = raw.trim();
  const at = email.indexOf('@');
  if (at <= 0) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const head = local[0] ?? '';
  return `${head}•••@${domain}`;
}

// =============================================================================
// register
// =============================================================================

/**
 * Зарегистрировать email-аккаунт. Требует Bearer (binding к текущему player_id);
 * если identity ещё нет — boot'им (как oauth-login). На 201 обновляем identity
 * новым secretToken (register сразу логинит, I-1) и возвращаем успех.
 *
 * **Вариант B (owner-decision 2026-06-21):** ник при регистрации НЕ задаётся — поле
 * `displayName` убрано из формы и из запроса; identity получает `displayName: null`
 * (ник вводится в Профиле, первый ввод там — бесплатно). Клиент НЕ хранит email/пароль —
 * они только в аргументах на момент вызова.
 */
export async function register(args: {
  email: string;
  password: string;
}): Promise<RegisterResult> {
  // Гарантируем identity (Bearer). register биндит local-credential к этому
  // player_id (гостевой preserved), поэтому без него нельзя.
  let identity = getCurrent();
  if (!identity) identity = await bootIdentity();
  if (!identity) return { ok: false, reason: 'no_identity' };

  let response;
  try {
    response = await apiClient.post<RegisterWire>('/auth/local/register', {
      email: args.email,
      password: args.password,
    });
  } catch {
    return { ok: false, reason: 'network' };
  }

  if (response.ok) {
    await updateIdentity({
      playerId: response.body.playerId,
      secretToken: response.body.secretToken,
      // Вариант B: ник NULL (не задан при регистрации) → Профиль покажет «Игрок» и
      // позволит первый бесплатный ввод (server-truth display_name IS NULL).
      displayName: null,
      authMethod: 'email',
      maskedEmail: maskEmail(args.email),
    });
    return {
      ok: true,
      playerId: response.body.playerId,
      displayName: null,
    };
  }
  return { ok: false, reason: mapRegisterError(response.status, response.body) };
}

function mapRegisterError(status: number, body: unknown): RegisterErrorReason {
  const reason = (body as ServerErrorBody)?.reason;
  if (status === 400) {
    if (
      reason === 'invalid_email' ||
      reason === 'weak_password' ||
      reason === 'invalid_display_name'
    ) {
      return reason;
    }
    return 'invalid_email';
  }
  if (status === 409) return 'email_taken';
  if (status === 401) return 'unauthorized';
  if (status === 503) return 'unavailable';
  return 'server_error';
}

// =============================================================================
// login
// =============================================================================

/**
 * Войти по email + паролю. БЕЗ обязательного Bearer (login получает auth). На 200
 * обновляем identity (secretToken резолвится к существующему аккаунту — кросс-девайс
 * возврат, §6.3). На 401 — обезличенный `credentials_invalid` (UI: «Неверная почта
 * или пароль», AC-6).
 */
export async function login(args: {
  email: string;
  password: string;
}): Promise<LoginResult> {
  let response;
  try {
    response = await apiClient.post<LocalAuthWire>('/auth/local/login', {
      email: args.email,
      password: args.password,
    });
  } catch {
    return { ok: false, reason: 'network' };
  }

  if (response.ok) {
    await updateIdentity({
      playerId: response.body.playerId,
      secretToken: response.body.secretToken,
      displayName: response.body.displayName,
      authMethod: 'email',
      maskedEmail: maskEmail(args.email),
    });
    return {
      ok: true,
      playerId: response.body.playerId,
      displayName: response.body.displayName,
    };
  }
  return { ok: false, reason: mapLoginError(response.status, response.body) };
}

function mapLoginError(status: number, body: unknown): LoginErrorReason {
  const reason = (body as ServerErrorBody)?.reason;
  if (status === 401) return 'credentials_invalid';
  if (status === 400) return 'malformed_request';
  if (status === 503) return 'unavailable';
  if (reason === 'credentials_invalid') return 'credentials_invalid';
  return 'server_error';
}
